import { create } from '../utils.mjs';
import { createPill, clonePill } from '../helpers/iml.mjs';
import { getCaret, isSpacer, setCaret } from './coder.helpers.mjs';
import { I18n } from '../helpers/i18n.mjs';
import fuzzysort from 'fuzzysort';

import { computePosition, offset, flip, autoUpdate } from '@floating-ui/dom';

import classes from './autoComplete.module.scss';
import Features from '../features.mjs';
import { isAiBlock, MAPAI_FUNCTION_LABEL, MAPAI_FUNCTION_NAME } from './aiBlock.mjs';

/**
 * @typedef {Object} Pill
 * @property {string} code
 * @property {string} name
 * @property {any} node
 * @property {string} prefix
 * @property {string} raw
 * @property {any} theme
 * @property {string} type
 */

/**
 * @typedef {Object} Module
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {Pill[]} pills
 */

/**
 * @typedef {Object} Popover
 * @property {() => void} open;
 * @property {() => void} close;
 * @property {HTMLElement} boid;
 */

/**
 * Creates a popover relative to am imt-coder-token
 *
 * @param {HTMLElement} relative
 * @return {Popover}
 */
const createPopover = (relative) => {
	const panel = create(`div.${classes.popover}`);
	const panelContent = create(`div.${classes.popoverContent}`);

	panel.appendChild(panelContent);

	const updatePosition = () => {
		computePosition(relative, panel, {
			placement: 'bottom-start',
			middleware: [
				offset({
					mainAxis: 6,
					crossAxis: -8, // Align imt-coder-token with pills
				}),
				flip(),
			],
		}).then(({ x, y }) => {
			Object.assign(panel.style, {
				left: `${x}px`,
				top: `${y}px`,
			});
		});
	};

	let cleanup = null;

	return {
		body: panelContent,
		open() {
			document.body.appendChild(panel);
			cleanup = autoUpdate(relative, panel, updatePosition);
		},

		close() {
			panel.remove();
			cleanup?.();
		},
	};
};

const eventManager = () => {
	const listeners = [];

	return {
		add: (target, eventName, handler, options) => {
			target.addEventListener(eventName, handler, options);
			listeners.push(() => target.removeEventListener(eventName, handler, options));
		},
		clear() {
			while (listeners.length) {
				listeners.pop()();
			}
		},
	};
};

/**
 * @typedef {Object} PillOptions
 * @property {string} name - pill name
 * @property {string | undefined} content - what should be displayed in the pill (use name if undefined)
 * @property {string} type - variable or function
 * @property {Object | undefined} theme - theme of the pill
 */

/**
 * @param {PillOptions} options
 * @return {HTMLElement}
 */
const createOptionPill = ({ name, type, content, theme, imlItems, inspector }) => {
	const elm = createPill(type, name, { imlItems, inspector });

	elm.textContent = content || name;

	elm.removeAttribute('draggable');
	elm.setAttribute('preview', true);

	if (theme) {
		elm.style.color = theme.determineForegroundColor();
		elm.style.backgroundColor = theme.toHex();
	}

	return elm;
};

/**
 * @typedef {Object} AutoCompleteItem
 * @property {string} key - unique identifier for each item
 * @property {boolean} visible - is the item displayed or not
 * @property {string} type - type of the item (not used internally - only for onSubmit callback)
 * @property {string} label - label used to search for items
 * @property {HTMLElement} ref - internal ref for rendered component
 * @property {Object} data - item data
 */

/**
 * @typedef {Object} ItemParam
 * @property {(AutoCompleteItem, HTMLElement) => HTMLElement | HTMLElement[]} render - create the list item and attach it to the list
 * @property {string} key - unique identifier of the item
 * @property {Object} data - stored data for the item
 * @property {string} label
 * @property {string} type
 */

/**
 * @typedef {Object} CreateCoderItemParams
 * @property {Module[]} modules
 * @property {Object.<string, HTMLElement>} pills
 * @property {unknown} imlItems
 * @property {unknown} inspector
 */

/**
 * Creates items to be used by the coder, the auto complete doesn't care about this function,
 * however the items need to have the correct format ({@link ItemParam})
 *
 * @param {CreateCoderItemParams} params
 * @return {ItemParam[]}
 */
export const createCoderAutoCompleteItems = ({ cache, modules, pillCategories, imlItems, inspector, parent }) => {
	const moduleItems = Object.keys(cache || {})
		.map((cacheKey) => {
			const pillElm = clonePill(cache[cacheKey]);
			const pill = pillElm._pill;
			const appId = pill?.node?.id;
			const appModule = modules.find((x) => x.id === appId);

			if (!appModule) {
				return;
			}

			const appName = appModule.name;
			const description = create('span.dmo-truncate');
			description.textContent = `${appName ?? ''} - ${appModule.description}`;

			const emptyCategory = create(`span.${classes['pill-category']}`);

			return {
				key: cacheKey,
				label: `${pillElm.getAttribute('prefix') || ''} ${pill.name}`,
				type: pill.type,
				data: pillElm,
				render: () => {
					return [pillElm, description, emptyCategory];
				},
			};
		})
		.filter((item) => item !== undefined);

	const categoryRendered = {
		function: 'Func',
		operator: 'Oper',
		scenario_variable: 'ScenVar',
		team_variable: 'TeamVar',
		organization_variable: 'OrgVar',
		keyword: 'kw',
		variable: 'Var',
		scenario_input: 'in',
	};
	const variableItems = Object.keys(pillCategories).flatMap((category) => {
		const pills = Object.values(pillCategories[category]);
		const result = [];

		for (const element of pills) {
			const name = element.getAttribute('name');
			const groups = element.getAttribute('group');
			const type = element.getAttribute('type');

			if (
				(!Features.isOn('coder-ai-block') || isAiBlock(parent)) &&
				category === 'function' &&
				name === MAPAI_FUNCTION_NAME
			) {
				continue;
			}

			// some functions have multiple groups and we need to represent them separately
			// eg contains has array/string
			for (const group of groups.split(',')) {
				const key = [group, type, element.textContent].join('-');
				const syntaxKey = `iml.${group}.functions.${name}.syntax`;
				const syntax = I18n.l(syntaxKey);
				const functionPill = createOptionPill({
					name,
					type: category,
					content: element.textContent,
					imlItems,
					inspector,
				});

				result.push({
					key,
					data: element,
					type: category,
					label: element.textContent,
					render: () => {
						if (element?._pill?.internal) {
							if (name === MAPAI_FUNCTION_NAME) {
								functionPill.textContent = MAPAI_FUNCTION_LABEL;
							}

							return [functionPill];
						}

						const syntaxElem = create(`span.dmo-font-roboto.${classes['syntax']}`);

						// if the syntax is missing in locale files, just print type to at least show something meaningful
						if (category === 'function' && syntax !== syntaxKey) {
							syntaxElem.textContent = syntax.replaceAll(`${name}`, '');
						} else {
							syntaxElem.textContent = `<${type}>`;
						}

						const categoryElm = create(
							`span.dmo-px-2.text-right.${classes['pill-category']} ${categoryRendered[category]}`,
						);

						return [functionPill, syntaxElem, categoryElm];
					},
				});
			}
		}
		return result;
	});

	return [...moduleItems, ...variableItems];
};

const replaceNodeWithInnerText = (relative) => {
	// We are removing the elment so so we also need to remove any spacers around the element
	[relative.nextSibling, relative.previousSibling].forEach((sibling) => {
		if (isSpacer(sibling)) {
			sibling.remove();
		}
	});

	// Inserts a new text element where the old one used to be
	const textNode = document.createTextNode(relative.textContent);
	relative.replaceWith(textNode);
	setCaret(textNode, textNode.textContent.length);
	textNode.parentElement.normalize();
};

const insertNodeInText = (relative) => {
	const caret = getCaret();

	if (caret.offset <= 0) {
		caret.node.before(relative);
	} else if (caret.node.nodeType === Node.TEXT_NODE && caret.offset < caret.node.textContent.length) {
		caret.node.splitText(caret.offset);
		caret.node.after(relative);
	} else {
		caret.node.after(relative);
	}

	setCaret(relative, relative.textContent.length);
};

/**
 * Create the state for each individual auto complete item
 * Also creates the dom element of the item without mounting it
 *
 * @param {ItemParam[]} itemParams
 * @return {AutoCompleteItem[]}
 */
const createAutoCompleteItemsState = (itemParams) => {
	return itemParams.map(({ key, data, type, label, render }) => {
		const ref = create(`li.${classes.listItem}`);

		const renderedItem = render(data, ref);

		if (renderedItem) {
			ref.append(...(Array.isArray(renderedItem) ? renderedItem : [renderedItem]));
		}

		return {
			key,
			type,
			label,
			visible: true,
			ref,
			data,
		};
	});
};

/**
 * @typedef {Object} CreateAutoCompleteOptions
 * @property {({ key: string }) => void} onSubmit
 * @property {ItemParam[]} items
 * @property {string[] | undefined} pinnedItemKeys
 *
 * @typedef {Object} CreateAutoCompleteResult
 * @property {() => void} remove
 */

/**
 * @param {CreateAutoCompleteOptions} options
 * @return {CreateAutoCompleteResult | null}
 */
export const createAutoComplete = (options) => {
	const items = createAutoCompleteItemsState(options.items);

	if (items.length === 0) {
		return null;
	}

	const events = eventManager();

	/** @type {AutoCompleteItem[]} - Items that should be displayed when no search was made */
	const pinnedItems = options.pinnedItemKeys
		? options.pinnedItemKeys
				.map((pinnedItemKey) => items.find((item2) => pinnedItemKey === item2.key) || null)
				.filter((item) => item != null)
		: [];

	/** @type {AutoCompleteItem | null} - Currently selected item */
	let selectedItem = null;
	/** @type {string} - Text that is inside the relative element */
	let text = '';
	/** @type {() => void | null} */
	let remove = null;
	/** @type {HTMLElement | null} */
	let relative = null;
	/** @type {HTMLElement | null} - list element that contains the rendered list items */
	let list = null;
	/** @type {Popover | null}  */
	let popover = null;
	/** @type {string | null} - value for which no results were found */
	let noResultsText = null;

	const noResultsThreshold = 1;
	const noResultsLabel = create(`span.${classes.label} ${I18n.l('autoComplete.noResults')}`);
	const startTypingLabel = create(`span.${classes.label} ${I18n.l('autoComplete.startTyping')}`);
	const dividerLine = create(`hr.${classes.dividerLine}`);
	const placeholder = create(`span.${classes.placeholder}`);
	placeholder.setAttribute('data-placeholder', I18n.l('autoComplete.searchPlaceholder'));

	/**
	 * Updates the selected items and the styling of both the new and previous selected items
	 * Scrolls new previous selected item into view
	 * @param {AutoCompleteItem | null} newSelectedItem
	 */
	const updateSelectedItem = (newSelectedItem) => {
		selectedItem?.ref.classList.remove(classes.listItemSelected);

		if (!newSelectedItem || !newSelectedItem.visible) {
			selectedItem = null;
			return;
		}

		selectedItem = newSelectedItem;

		selectedItem.ref.classList.add(classes.listItemSelected);
		selectedItem.ref.scrollIntoView({ block: 'nearest' });
	};

	/**
	 * Correctly displays and sorts items based on current auto complete search
	 */
	const updateItems = () => {
		const search = text.substring(1);
		const hasSearch = !!search.trim();

		const results = fuzzysort.go(search, items, {
			// The score for the label should be prioritized by the fuzzysort library
			keys: ['label', 'ref.textContent'],
			threshold: 0.5,
		});

		items.forEach((item) => {
			item.visible = false;
		});

		// Valid results are added to the end of the list in order
		results.forEach(({ obj: item }) => {
			item.visible = true;
			list.appendChild(item.ref);
		});

		// If there is no search, we show the pinned items first
		if (!hasSearch) {
			pinnedItems.forEach((item) => {
				item.visible = true;
				list.appendChild(item.ref);
			});
			updateSelectedItem(null);
		}

		// Remove all other items that still not visible
		items.forEach((item) => {
			if (!item.visible) {
				item.ref.remove();
			}
		});

		startTypingLabel.classList.toggle('dmo-hidden', hasSearch);

		if (hasSearch) {
			placeholder.remove();
		} else {
			relative.append(placeholder);
		}

		dividerLine.classList.toggle('dmo-hidden', hasSearch);

		const shouldHideNoResultsLabel = !hasSearch || results.length > 0;
		noResultsLabel.classList.toggle('dmo-hidden', shouldHideNoResultsLabel);
		if (!shouldHideNoResultsLabel) {
			updateSelectedItem(null);
		}

		if (results.length === 0) {
			noResultsText = noResultsText ?? text;
		} else {
			noResultsText = null;
		}

		// Use the first rendered item, not the first item in items
		const getFirstVisibleItem = () => {
			const firstItemRef = list.firstChild;
			const firstSelectedItem = items.find(({ ref }) => firstItemRef == ref);

			return firstSelectedItem;
		};

		const nextSelectedItem = getFirstVisibleItem();
		if (nextSelectedItem && hasSearch) {
			updateSelectedItem(nextSelectedItem);
		}
	};

	const cancel = () => {
		events.clear();
		replaceNodeWithInnerText(relative);
		remove?.();
	};

	// When the user is searching/scrolling using the keyboard, mouse events can still be triggered while the items are repositioned
	// This stops these events from happening
	let mouseInteractionsDisabled = null;
	const disableMouseInteractions = () => {
		if (mouseInteractionsDisabled) {
			clearTimeout(mouseInteractionsDisabled);
		}

		mouseInteractionsDisabled = setTimeout(() => {
			mouseInteractionsDisabled = null;
		}, 200);
	};

	const build = () => {
		relative = create('imt-coder-token[contenteditable=true] /');
		relative.classList.add(classes.autoCompleteToken);
		relative.onRemove = () => {
			remove?.();
		};

		text = relative.textContent;

		insertNodeInText(relative);

		list = create(`ul.${classes.list}`);

		items.forEach((item) => {
			events.add(item.ref, 'mouseenter', () => {
				if (mouseInteractionsDisabled !== null) {
					return;
				}

				updateSelectedItem(item);
			});

			events.add(item.ref, 'mousedown', (ev) => {
				remove?.();

				options.onSubmit(item);
			});

			list.appendChild(item.ref);
		});

		updateItems();

		const keyboardHint = create(`div.${classes.keyboardHint}`);
		const keyboardKey = create('i.far.fa-xs.fa-arrow-turn-down-left').outerHTML;
		keyboardHint.innerHTML = I18n.l('autoComplete.keyboardHint', { key: keyboardKey }, true);

		popover = createPopover(relative);

		if (pinnedItems.length) {
			popover.body.append(startTypingLabel, dividerLine, list, noResultsLabel);

			startTypingLabel.classList.add(classes['label--pinned']);
			noResultsLabel.classList.add(classes['label--pinned']);
			popover.body.classList.add(classes['popoverContent--pinned']);
			list.classList.add(classes['list--pinned']);
		} else {
			popover.body.append(list, startTypingLabel, noResultsLabel, keyboardHint);
		}
		popover.body.addEventListener('mousedown', (event) => {
			event.preventDefault();
		});
		popover.open();

		events.add(document, 'selectionchange', () => {
			const { anchorNode: node } = document.getSelection();

			// Make sure that user does not change selection outside of this imt-coder-token
			if (node.parentElement !== relative) {
				cancel();
			}
		});

		/**
		 * Gets the next visible item, returning it's absolute index in the `items` array
		 *
		 * @param {boolean} direction - true for increasing and false for decreasing
		 * @return {AutoCompleteItem}
		 */
		const getNextSelectedItem = (direction) => {
			if (!selectedItem) {
				return items.find(({ ref }) => ref === (direction ? list.firstChild : list.lastChild)) || null;
			}
			const currentItem = selectedItem.ref;

			// Use the DOM to find the next/prev element because it is correctly sorted
			let nextElement = null;
			if (direction) {
				nextElement = currentItem.nextSibling || currentItem.parentElement.firstChild;
			} else {
				nextElement = currentItem.previousSibling || currentItem.parentElement.lastChild;
			}

			const item = items.find(({ ref }) => ref === nextElement);

			return item || null;
		};

		const coder = relative.closest('imt-coder');
		events.add(coder, 'keydown', (ev) => {
			switch (ev.code) {
				case 'ArrowDown': {
					ev.preventDefault();
					const nextItem = getNextSelectedItem(true);
					updateSelectedItem(nextItem);
					disableMouseInteractions();
					break;
				}

				case 'ArrowUp': {
					ev.preventDefault();
					const previousItem = getNextSelectedItem(false);
					updateSelectedItem(previousItem);
					disableMouseInteractions();
					break;
				}

				case 'Tab':
				case 'Enter': {
					ev.preventDefault();

					if (!selectedItem || !selectedItem.visible) {
						cancel();
						return;
					}

					remove?.();
					options.onSubmit(selectedItem);
					break;
				}

				case 'Backspace':
					// Cancel if backspace is pressed and there is no text but keep slash
					if (text === '/') {
						ev.preventDefault();
						cancel();
					}
			}
		});

		// Remove auto complete on keyup to prevent inspector panel from closing at the same time
		events.add(coder, 'keyup', (ev) => {
			if (ev.code === 'Escape') {
				ev.stopPropagation();
				cancel();
			}
		});

		events.add(coder, 'input', () => {
			text = relative.textContent;

			// Cancel if user inputs another slash after initial slash (makes it easier to work with urls like http://)
			if (text == '//') {
				cancel();
				return;
			}

			// Cancel if there have been no results and text increased a certain number of characters
			if (noResultsText && text.length > noResultsText.length + noResultsThreshold) {
				cancel();
				return;
			}
			// Cancel if last 2 tokens are empty spaces
			if (/\s\s$/.test(text)) {
				cancel();
				return;
			}

			disableMouseInteractions();
			updateItems();
		});
	};

	remove = () => {
		events.clear();
		popover?.close();
		relative?.remove();
	};

	build();

	return {
		remove,
	};
};
